import React, { useEffect, useState } from 'react';
import { Col, Progress, Row } from 'antd';
import Icon from 'assets/Icons';
import Box from 'components/Box';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import Modal from 'components/Modal';
import Text from 'components/Text';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import StaffBoxChange from 'features/turn/pages/components/StaffBoxChange';
import TypeOptions from 'features/turn/pages/components/TypeOptions';
import turnActions, { turnManualAPIActions } from 'features/turn/services/actions';
import turnSelectors from 'features/turn/services/selectors';
import moment from 'moment';
import Countdown from 'react-countdown';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { styled } from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import storage from 'utils/sessionStorage';
import RequestAdminLocalPW from 'widgets/RequestAdminLocalPW';
import { TagBgColor, TurnStatus } from '../constanst';
import { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import ModalConfirm from 'components/Modal/ModalConfirm';
import useTabletApp from 'hooks/useTabletApp';
dayjs.extend(customParseFormat);

type IModalDetail = {
  isShow: boolean;
  onClose: () => void;
  v2?: boolean;
};
const ModalDetail: React.FC<IModalDetail> = ({ isShow, onClose, v2 = false }) => {
  const tabletApp = useTabletApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const modalConfirm = useModalConfirmRef();
  const [selectedStaff, setSelectedStaff] = useState<string>();
  const [selectedTurn, setSelectedTurn] = useState<number>();
  const [selectedType, setSelectedType] = useState<string>();
  const [showPasswordWithStatus, setShowPassword] = useState<string | null>(null);
  const turnDetail = turnSelectors.getTurnDetail();

  useEffect(() => {
    if (turnDetail && isShow) {
      setSelectedStaff(turnDetail.staffId);
      setSelectedTurn(turnDetail.turn);
      setSelectedType(turnDetail.type);
    }
  }, [turnDetail]);
  const onChangeSelectStaff = (value: any) => {
    setSelectedStaff(value);
  };
  const onChangeSelectTurn = (value: any) => {
    setSelectedTurn(value);
  };
  const onChangeSelectType = (value: any) => {
    setSelectedType(value);
  };
  const renderProgress = (status: string, value?: number) => {
    switch (status) {
      case TurnStatus.DONE:
        return <Icon type='turnFinish' width={85} height={85} />;
      case TurnStatus.VOIDED:
        return (
          <Progress type='circle' showInfo={false} percent={100} size={85} strokeColor='#FEEDBF' />
        );
      case TurnStatus.FINISH_TURN:
        return (
          <Progress type='circle' showInfo={false} percent={100} size={85} strokeColor='#FF7D00' />
        );
      case TurnStatus.IN_PROGRESS:
      default:
        return (
          <Progress
            type='circle'
            showInfo={false}
            percent={value || 95}
            size={85}
            strokeColor='#FF8890'
          />
        );
    }
  };
  const renderStatusBar = (status: string) => {
    switch (status) {
      case TurnStatus.DONE:
        return (
          <StatusBarCompleted>
            <Text variant='H5'>Completed Ticket {turnDetail?.endTime || ''} </Text>
          </StatusBarCompleted>
        );
      case TurnStatus.VOIDED:
        return (
          <StatusBarVoided>
            <Text variant='H5'>VOIDED</Text>
          </StatusBarVoided>
        );
      case TurnStatus.FINISH_TURN:
        return (
          <StatusBarPending>
            <Text variant='H5'>Finished Turn</Text>
          </StatusBarPending>
        );
      case TurnStatus.OUT:
        return (
          <StatusBarPending>
            <Text variant='H5'>Out</Text>
          </StatusBarPending>
        );
      case TurnStatus.IN_PROGRESS:
      default:
        return (
          <StatusBarProgress>
            <Text variant='H5'>Working In Progress ...</Text>
          </StatusBarProgress>
        );
    }
  };
  const onPay = () => {
    onClose();
    if(tabletApp.enable) {
      tabletApp.cashierTicket(turnDetail.billId);
      return;
    }
    navigate(`/store/${storage.shop_id.get()}/ticket/edit/${turnDetail.billId}`);
  };

  const getTurnType = (_selectedTurn?: number) => {
    const value = _selectedTurn?.toString();
    if (value == '1') return 'FULL';
    if (value == '0.5') return 'HALF';
    return 'ZERO';
  };
  const onOK = (pinCode?: string) => {
    if (!pinCode) return;
    setShowPassword(null);

    const params = {
      staffId: selectedStaff || '',
      turnId: turnDetail.turnId,
      turn: getTurnType(selectedTurn),
      type: selectedType?.replace('-', '_') || '',
      // turnStatus: isFinishTurn.current ? TurnStatus.FINISH_TURN : TurnStatus.IN_PROGRESS,
      turnStatus: showPasswordWithStatus || '',
      pinCode,
    };
    dispatch(turnActions.updateTurnDetail.fetch(params));
  };

  const onUndo = () => {
    modalConfirm.current?.open('Do you like to undo this turn?', () =>
      dispatch(turnManualAPIActions.undoTurn.fetch(turnDetail.turnId))
    );
  };

  const renderFooterButton = (status: string) => {
    const BUTTON_DELETE = (
      <Button
        cardCashierNormal
        style={{ backgroundColor: '#fff' }}
        ntype='GHOST'
        onClick={() => setShowPassword(TurnStatus.VOIDED)}
      >
        <span style={v2 ? { fontSize: '1.2rem' } : {}}>Delete</span>
      </Button>
    );
    switch (status) {
      case TurnStatus.VOIDED:
        return (
          <>
            <ButtonFull>
              <Button cardCashier={v2} ntype='SECONDARY' onClick={onClose}>
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Close</span>
              </Button>
            </ButtonFull>
            <ButtonFull>{BUTTON_DELETE}</ButtonFull>
          </>
        );
      case TurnStatus.DONE && TurnStatus?.OUT:
        return (
          <>
            <ButtonFull>
              <Button cardCashier={v2} ntype='SECONDARY' onClick={onClose}>
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Close</span>
              </Button>
            </ButtonFull>
            <ButtonFull>{BUTTON_DELETE}</ButtonFull>
            <ButtonFull>
              <Button
                cardCashier={v2}
                ntype='PRIMARY'
                onClick={() => setShowPassword(TurnStatus.DONE)}
              >
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Update</span>
              </Button>
            </ButtonFull>
          </>
        );

      case TurnStatus.FINISH_TURN:
        return (
          <>
            <ButtonGroup>
              <Button cardCashier={v2} ntype='SECONDARY' onClick={onClose}>
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Close</span>
              </Button>
              {BUTTON_DELETE}
              <Button
                cardCashier={v2}
                ntype='PRIMARY'
                onClick={() => setShowPassword(TurnStatus.FINISH_TURN)}
              >
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Update</span>
              </Button>
              <Button
                disabled={!!turnDetail?.manual}
                cardCashier={v2}
                ntype='GREEN'
                onClick={onPay}
              >
                <span style={v2 ? { fontSize: '1.2rem', color: '#FFF' } : { color: '#FFF' }}>
                  Checkout
                </span>
              </Button>
              <Button cardCashier={v2} ntype='SECONDARY' onClick={onUndo}>
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Undo</span>
              </Button>
            </ButtonGroup>
          </>
        );
      case TurnStatus.IN_PROGRESS:
      default:
        return (
          <>
            <ButtonGroup>
              <Button cardCashier={v2} ntype='SECONDARY' onClick={onClose}>
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Close</span>
              </Button>
              {BUTTON_DELETE}
              <Button
                cardCashier={v2}
                ntype='LIGHT_BLUE'
                onClick={() => setShowPassword(TurnStatus.IN_PROGRESS)}
              >
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Update</span>
              </Button>
              <Button
                disabled={!!turnDetail?.manual}
                cardCashier={v2}
                ntype='GREEN'
                onClick={onPay}
              >
                <span
                  style={
                    v2
                      ? { fontSize: '1.2rem', color: turnDetail?.manual ? '#000' : '#FFF' }
                      : { color: turnDetail?.manual ? '#000' : '#FFF' }
                  }
                >
                  Checkout
                </span>
              </Button>
              <Button
                cardCashier={v2}
                ntype='ORANGE'
                onClick={() => setShowPassword(TurnStatus.FINISH_TURN)}
              >
                <span style={v2 ? { fontSize: '1.2rem' } : {}}>Finish Turn</span>
              </Button>
            </ButtonGroup>
          </>
        );
    }
  };

  const disabledConditionList = [TurnStatus.DONE, TurnStatus.FINISH_TURN];
  return (
    <>
      <Modal
        modalTitle={turnDetail?.staffName}
        visible={isShow}
        onClose={onClose}
        destroyOnClose={true}
        cancelTitle='Close'
        footer={<>{renderFooterButton(turnDetail?.status)}</>}
        noneBodyStyle
        v2={v2}
        width={v2 ? 1000 : undefined}
      >
        <Content style={v2 ? { zoom: 1.5 } : {}}>
          {turnDetail?.status === TurnStatus.IN_PROGRESS && (
            <InlineRow>
              <RowCus>
                <IconCus type='time' width={30} height={30} />{' '}
                <Text className='text-bolder'>{moment().format('MM-DD-YYYY')}</Text>
                <Text variant='H9' className='text-bolder'>
                  {turnDetail?.status === TurnStatus.IN_PROGRESS ? (
                    <Countdown
                      date={moment(turnDetail?.startTime)
                        .add(turnDetail?.countDown, 'minute')
                        .toISOString()}
                      daysInHours
                    />
                  ) : (
                    '00:00:00'
                  )}
                </Text>
              </RowCus>
            </InlineRow>
          )}
          <InlineRow>
            <FlexCol>
              <InlineRow>
                <RowCus>
                  <IconCus type='userSquare' width={30} height={30} />{' '}
                  <Text className='text-bolder'>{turnDetail?.customerName}</Text>
                </RowCus>
              </InlineRow>
              <InlineRow>
                <RowCus>
                  <IconCus type='ticket' width={30} height={30} />{' '}
                  <Tag variant='H9' color='text_1' background={'#000'}>
                    #{turnDetail?.billNumber}
                  </Tag>
                  <Text
                    variant='CONTENT_2'
                    style={{ fontSize: '1.5rem', fontWeight: 'bolder', marginLeft: '1.5rem' }}
                  >
                    {formatCurrency(turnDetail?.amount)}
                  </Text>
                </RowCus>
              </InlineRow>
            </FlexCol>
            <ProgressBox>{renderProgress(turnDetail?.status)}</ProgressBox>
          </InlineRow>
          <InlineRow>
            <RowCus>
              <IconCus type='listStatus' width={30} height={30} />
              {renderStatusBar(turnDetail?.status)}
            </RowCus>
          </InlineRow>

          {!(turnDetail?.status == 'PENDING') ? (
            <>
              <Col>
                <Label className='text-bolder fs'>STAFF</Label>
                <StaffBoxChange value={selectedStaff} onChange={onChangeSelectStaff} />
              </Col>
              <Col>
                <Label className='text-bolder fs'>TURN</Label>
                <Box display='flex' alignItems='center' gap={'4'} pb='1'>
                  <Checkbox
                    style={{ flex: 1 }}
                    label='Full Turn'
                    value={selectedTurn == 1}
                    onChange={(value) => onChangeSelectTurn(value ? 1 : 0.5)}
                    disabled={!disabledConditionList.includes(turnDetail?.status)}
                  />
                  <Checkbox
                    style={{ flex: 1 }}
                    label='Half Turn'
                    value={selectedTurn == 0.5}
                    onChange={(value) => onChangeSelectTurn(value ? 0.5 : 1)}
                    disabled={!disabledConditionList.includes(turnDetail?.status)}
                  />

                  <Checkbox
                    style={{ flex: 1 }}
                    label='Zero Turn'
                    value={selectedTurn == 0}
                    onChange={(value) => onChangeSelectTurn(value ? 0 : 1)}
                    disabled={!disabledConditionList.includes(turnDetail?.status)}
                  />
                </Box>
              </Col>
              <Col>
                <Label className='text-bolder fs'>SELECT TURN TYPE</Label>
                <TypeOptions value={selectedType} onChange={onChangeSelectType} />
              </Col>
            </>
          ) : (
            <></>
          )}
        </Content>
      </Modal>
      {!!showPasswordWithStatus && (
        <RequestAdminLocalPW
          onlyAdmin
          visible
          v2
          onCorrect={onOK}
          onClose={() => setShowPassword(null)}
        />
      )}
      <ModalConfirm ref={modalConfirm} />
    </>
  );
};

export default ModalDetail;

const Content = styled.div`
  padding: 0.5rem 2rem;
  .text-bolder {
    font-size: 1.5rem;
    font-weight: bolder;
    &.fs {
      font-size: 1rem;
    }
  }
`;

const InlineRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  margin: 10px 0px;
  .selector-cus {
    border-radius: 2px;
  }
  .staff-date-picker {
    border-radius: 2px;
  }
`;

const Label = styled(Text)`
  padding-top: 10px;
  padding-bottom: 5px;
  font-size: 1.5rem;
  font-weight: bolder;
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProgressBox = styled(FlexCol)`
  align-items: center;
  justify-content: center;
`;

const StatusBarProgress = styled.div<{}>`
  border-radius: 5px;
  background: var(--primary-button, #ffe5e7);
  display: flex;
  padding: 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1;
`;
const StatusBarVoided = styled(StatusBarProgress)`
  background: #feedbf;
`;
const StatusBarCompleted = styled(StatusBarProgress)`
  background: var(--blue-headline, #c6e9ef);
`;
const StatusBarPending = styled(StatusBarProgress)`
  background: var(--yellow-headline, #feedbf);
`;
const IconCus = styled(Icon)`
  padding-right: 1rem;
`;

const Tag = styled(Text)<{ background?: string }>`
  border-radius: 5px;
  background: ${(props) => (props?.background ? props.background : TagBgColor.WALK_IN)};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0px !important;
`;

const RowCus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  div {
    padding-right: 2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
  margin: 20px 0px;
  padding: 0px 20px;
  button {
    flex: 1;
  }
`;

const ButtonFull = styled(Row)`
  display: flex;
  width: 100%;
  margin: 20px 0px;
  padding: 0px 20px;
  button {
    width: 100%;
  }
`;
